import {max, min} from '../agg'
import {solveForV} from '../solver'
import {notNull} from '../type'
import {Dist} from './distributions'
import plot from 'simple-ascii-chart'
const {exp, pow} = Math

export namespace DistLogistic {
  export type LogisticCfg = {
    location?: number | undefined
    scale?: number | undefined
    skew?: number | undefined
    amplitude?: number | undefined
    /** hard bounds. Not just for display! **/
    hardBounds?: Bounds
  } & Dist.WithTails

  export const defaultLogistic = {
    /** the middle point */
    location: 0,
    /**  the size of 1 standard deviation */
    scale: 1,
    /** positive values make the hump part towards the right */
    skew: 0,
    amplitude: 1
  } satisfies LogisticCfg

  /**
   * Skewed Logistic probability density function
   * https://www.ine.pt/revstat/pdf/rs130305.pdf
   */
  export const probabilityDensitySkewLogistic = (props?: LogisticCfg) => {
    const {location, scale, skew, amplitude, hardBounds} = {...defaultLogistic, ...props}
    const {min, max} = hardBounds ?? {}
    return (x: number) => {
      if ((min != null && x < min) || (max != null && x > max)) return 0
      const ex = (a?: number) => exp((-(a ?? 1) * (x - location)) / scale)
      // Check if any exponential term would be too large
      const baseExp = (x - location) / scale
      if (Math.abs(baseExp) > 700) return 0 // exp(+/-700) is near the limit of JavaScript's number precision
      const res = amplitude * ((2 * ex()) / (pow(1 + ex(), 2) * (1 + ex(skew))))
      if (isNaN(res)) console.warn('probabilityDensitySkewLogistic: NaN', {x, location, scale, skew, amplitude})
      return res
    }
  }

  type Bounds = {min?: number; max?: number}
  // type DisplayBounds = {min: number; max: number}

  export const sdWidthsToGenerate = 7
  /** creates a distribution given the cfg, you need to add labels explicitly.
   * Remember the bounds of cfg ar hard!
   */
  export const logisticToPdf = (
    cfg: LogisticCfg,
    options?: {displayBounds?: Bounds; debugName?: string}
  ): Dist.PdfForAgg => {
    const {location, scale, hardBounds, left, right} = {...defaultLogistic, ...cfg}
    // find the range to generate a standard 0 location pd with the given skew.
    const radius = sdWidthsToGenerate * scale
    const bounds: [number, number] = [
      hardBounds?.min ?? min([options?.displayBounds?.min, location - radius].filter(notNull)),
      hardBounds?.max ?? max([options?.displayBounds?.max, location + radius].filter(notNull))
    ]
    return {type: 'logistic', bounds, getP: probabilityDensitySkewLogistic(cfg), left, right}
  }

  export const fitSkewLogistic = ({x10, x50, x90}: {x10: number; x50: number; x90: number}): LogisticCfg => {
    const getPAccRatio = ({x10, x50, x90}: {x10: number; x50: number; x90: number}) => (x90 - x50) / (x50 - x10)

    const getPAccRatioForDist = ({skew}: Pick<LogisticCfg, 'skew'>) => {
      const dist = Dist.pdfToDiscrete(logisticToPdf({skew}))
      const x10 = dist.percentileX(0.1)
      const x50 = dist.percentileX(0.5)
      const x90 = dist.percentileX(0.9)
      if (x10 == null || x50 == null || x90 == null) {
        console.warn('getPAccRatioForDist: x10, x50 or x90 is null', {dist, x10, x50, x90})
        return undefined
      }
      const res = (x90 - x50) / (x50 - x10)

      return res
    }

    const desiredPAccRatio = getPAccRatio({x10, x50, x90}) // the max we can achieve is around 2. So we need to be able to shortcut this.
    const skew = solveForV(skew => ({result: getPAccRatioForDist({skew})}), desiredPAccRatio, {
      startV: 0,
      clampV: {min: -50, max: 50}
    }).v

    const getWidthOf10to90 = (cfg: Pick<LogisticCfg, 'skew' | 'scale'>) => {
      const dist = Dist.pdfToDiscrete(logisticToPdf(cfg))
      const x10 = dist.percentileX(0.1)
      const x90 = dist.percentileX(0.9)
      if (x10 == null || x90 == null) return undefined
      return x90 - x10
    }
    const scale = solveForV(scale => ({result: getWidthOf10to90({skew, scale})}), x90 - x10, {startV: 1}).v

    const locationRes = solveForV(
      location => {
        const dist = Dist.pdfToDiscrete(logisticToPdf({location, scale, skew}))
        return {result: dist.percentileX(0.5), dist}
      },
      x50,
      {
        startV: x50
      }
    )

    return {location: locationRes.v, scale, skew}
  }
}

const plotDist = (dist: Dist.DiscreteDistribution, options?: {y: 'p' | 'pAcc'}) =>
  plot(
    dist.pts.map((p): [number, number] => [p.x, options?.y == 'pAcc' ? p.pAcc : p.p]),
    {height: 20, width: 140, formatter: (n, {axis}) => (axis == 'x' ? n.toFixed(1) : n.toFixed(3))}
  )
function compact(arg0: (number | undefined)[]): number[] {
  throw new Error('Function not implemented.')
}
